import React, { useCallback, useEffect, useState } from 'react';
import { Container, TableTitle } from '../components';
import ConfirmAlert from '../components/ConfirmAlert';
import AlertAccent from '../components/AlertAccent';
import LoadingSpinner from '../components/LoadingSpinner';
import IdleState from '../features/firmwareTest/IdleState';
import CampaignState from '../features/firmwareTest/CampaignState';
import { useFirmwareTestStore } from '../features/firmwareTest/state/use-firmware-test-store';
import { POLL_INTERVAL_MS, START_CONFIRM_TEXT } from '../features/firmwareTest/constants';

const CONFIRM_COPY = {
  start: {
    title: 'Запуск обкатки',
    confirmLabel: 'Запустить',
    text: START_CONFIRM_TEXT,
  },
  verify: {
    title: 'Запуск второго этапа',
    confirmLabel: 'Запустить',
    text: 'Будет запущен переход с тестовой-1 на выбранную тестовую-2.',
  },
  promote: {
    title: 'Выкатка на весь парк',
    confirmLabel: 'Выкатить',
    text: '',
  },
  cancel: {
    title: 'Отмена обкатки',
    confirmLabel: 'Отменить',
    text: 'Разрешения на обновление будут сняты, кампания закроется. Прошивка останется в тестовом канале, на боевой парк отмена не повлияет.',
  },
  exclude: {
    title: 'Вывести устройство из обкатки',
    confirmLabel: 'Вывести',
    text: 'Устройство перестанет блокировать этап. Признак «тестовый» сохранится.',
  },
};

const FirmwareTest = () => {
  const {
    status,
    firmware,
    testDevices,
    isLoading,
    error,
    firmwareError,
    actionLoading,
    excludingId,
    loadInitial,
    fetchStatus,
    fetchFirmware,
    startCampaign,
    verifyCampaign,
    promoteCampaign,
    cancelCampaign,
    excludeDevice,
  } = useFirmwareTestStore();
  const [confirm, setConfirm] = useState(null);

  const campaign = status?.campaign;
  const campaignStatus = campaign?.status;

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (!campaignStatus || campaignStatus === 'ready') return undefined;

    const timer = setInterval(() => {
      fetchStatus({ silent: true });
      if (campaignStatus === 'verify_pending') fetchFirmware();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [campaignStatus, fetchStatus, fetchFirmware]);

  const closeConfirm = () => setConfirm(null);

  const handleConfirm = async () => {
    if (!confirm || actionLoading) return;

    const actions = {
      start: () => startCampaign(confirm.version),
      verify: () => verifyCampaign(confirm.version),
      promote: () => promoteCampaign(),
      cancel: () => cancelCampaign(),
      exclude: () => excludeDevice(confirm.deviceId),
    };

    const ok = await actions[confirm.type]?.();
    if (ok) closeConfirm();
  };

  const handleRefresh = useCallback(() => {
    fetchStatus({ silent: true });
    fetchFirmware();
  }, [fetchStatus, fetchFirmware]);

  const confirmMeta = confirm ? CONFIRM_COPY[confirm.type] : null;
  const promoteVersion = campaign?.verify_version;
  const confirmText =
    confirm?.type === 'promote'
      ? `Действие необратимое. В стабильный канал уедет версия ${promoteVersion}.`
      : confirm?.type === 'start'
        ? `Будет запущена обкатка версии ${confirm.version}. ${confirmMeta?.text}`
        : confirm?.type === 'verify'
          ? `Будет запущена обкатка версии ${confirm.version}. ${confirmMeta?.text}`
          : confirm?.type === 'exclude'
            ? `Вывести ${confirm.serialNumber || 'устройство'} из текущей обкатки? ${confirmMeta?.text}`
            : confirmMeta?.text;

  return (
    <Container>
      <TableTitle title="Обкатка прошивок" />

      {isLoading ? (
        <LoadingSpinner style="h-[400px] flex items-center justify-center" loading={true} />
      ) : error ? (
        <div className="max-w-7xl mx-auto bg-white shadow-md rounded-3xl p-4 mt-6">
          <p className="text-red-500 text-center">Ошибка: {error.error || 'Не удалось загрузить данные обкатки'}</p>
        </div>
      ) : campaign ? (
        <CampaignState
          status={status}
          firmware={firmware}
          firmwareError={firmwareError}
          actionLoading={actionLoading}
          excludingId={excludingId}
          onVerify={(version) => setConfirm({ type: 'verify', version })}
          onPromote={() => setConfirm({ type: 'promote' })}
          onCancel={() => setConfirm({ type: 'cancel' })}
          onExclude={(device) => setConfirm({ type: 'exclude', deviceId: device.device_id, serialNumber: device.serial_number })}
          onRefresh={handleRefresh}
        />
      ) : (
        <IdleState
          firmware={firmware}
          firmwareError={firmwareError}
          testDevices={testDevices}
          actionLoading={actionLoading}
          onStart={(version) => setConfirm({ type: 'start', version })}
        />
      )}

      <ConfirmAlert
        showAlert={Boolean(confirm)}
        setShowAlert={(open) => !open && closeConfirm()}
        delCb={handleConfirm}
        confirmLabel={confirmMeta?.confirmLabel}
        title={confirmMeta?.title}>
        <AlertAccent text={confirmText} showAlert={true} />
      </ConfirmAlert>
    </Container>
  );
};

export default FirmwareTest;
